import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';

const DEFAULT_CHART_COLORS = [
    '#006D43',
    '#2563EB',
    '#7C3AED',
    '#D97706',
    '#E11D48',
    '#0891B2',
    '#C026D3',
    '#EA580C',
];

const parsePieItem = (item, idx, labels = []) => {
    if (typeof item === 'object' && item !== null) {
        const val = Number(item.value ?? item.amount ?? 0);
        const lbl = item.label || item.name || item.category || labels[idx] || `Item ${idx + 1}`;
        const color = item.color || DEFAULT_CHART_COLORS[idx % DEFAULT_CHART_COLORS.length];
        return {
            id: item.id !== undefined ? item.id : idx,
            value: isNaN(val) ? 0 : val,
            label: String(lbl),
            color,
        };
    }
    const val = Number(item || 0);
    return {
        id: idx,
        value: isNaN(val) ? 0 : val,
        label: String(labels[idx] || `Item ${idx + 1}`),
        color: DEFAULT_CHART_COLORS[idx % DEFAULT_CHART_COLORS.length],
    };
};

const AdvisorChart = ({ config }) => {
    if (!config || typeof config !== 'object') {
        return null;
    }

    const { type = 'bar', title, xAxis = [], series = [], data = [] } = config;

    // 1. Pie Chart
    if (type.toLowerCase() === 'pie') {
        const labels = xAxis[0]?.data || config.labels || [];
        let rawItems = [];
        if (Array.isArray(data) && data.length > 0) {
            rawItems = data;
        } else if (Array.isArray(series) && series[0]?.data) {
            rawItems = series[0].data;
        }

        const pieData = rawItems.map((item, idx) => parsePieItem(item, idx, labels));

        if (!pieData.length || pieData.every(p => p.value === 0)) {
            return null;
        }

        return (
            <Paper
                elevation={0}
                sx={{
                    my: 2,
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 12px rgba(28, 28, 24, 0.04)',
                }}
            >
                {title && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: '"Manrope", sans-serif', letterSpacing: '-0.01em', mb: 1.5, color: 'text.primary' }}>
                        {title}
                    </Typography>
                )}
                <Box sx={{ width: '100%', height: 260, display: 'flex', justifyContent: 'center' }}>
                    <PieChart
                        series={[
                            {
                                data: pieData,
                                innerRadius: 40,
                                outerRadius: 90,
                                paddingAngle: 2,
                                cornerRadius: 4,
                                highlightScope: { fade: 'global', highlight: 'item' },
                            },
                        ]}
                        height={260}
                        margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    />
                </Box>
            </Paper>
        );
    }

    // 2. Bar Chart
    if (type.toLowerCase() === 'bar') {
        let xLabels = xAxis[0]?.data || config.labels || [];
        let barSeries = series;

        if (Array.isArray(data) && data.length > 0) {
            if (!xLabels.length) {
                xLabels = data.map((d, i) => (typeof d === 'object' && d !== null ? d.name || d.label || d.category || `Item ${i + 1}` : `Item ${i + 1}`));
            }
            const values = data.map(d => (typeof d === 'object' && d !== null ? Number(d.value ?? d.amount ?? 0) : Number(d || 0)));
            barSeries = [{ data: values, label: title || 'Amount' }];
        } else if (Array.isArray(series) && series.length > 0) {
            barSeries = series.map((s, sIdx) => {
                const sData = Array.isArray(s.data) ? s.data : [];
                if (sData.length > 0 && typeof sData[0] === 'object' && sData[0] !== null) {
                    if (!xLabels.length) {
                        xLabels = sData.map((d, i) => d.name || d.label || d.category || `Item ${i + 1}`);
                    }
                    const numValues = sData.map(d => Number(d.value ?? d.amount ?? 0));
                    return { ...s, data: numValues, color: s.color || DEFAULT_CHART_COLORS[sIdx % DEFAULT_CHART_COLORS.length] };
                }
                const numValues = sData.map(v => Number(v || 0));
                return { ...s, data: numValues, color: s.color || DEFAULT_CHART_COLORS[sIdx % DEFAULT_CHART_COLORS.length] };
            });
        }

        const formattedXAxis = [
            {
                data: xLabels.length ? xLabels : ['Item 1'],
                scaleType: 'band',
            },
        ];

        return (
            <Paper
                elevation={0}
                sx={{
                    my: 2,
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 12px rgba(28, 28, 24, 0.04)',
                }}
            >
                {title && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: '"Manrope", sans-serif', letterSpacing: '-0.01em', mb: 1.5, color: 'text.primary' }}>
                        {title}
                    </Typography>
                )}
                <Box sx={{ width: '100%', height: 280 }}>
                    <BarChart
                        xAxis={formattedXAxis}
                        series={barSeries}
                        height={280}
                        borderRadius={6}
                        margin={{ top: 20, bottom: 40, left: 50, right: 20 }}
                    />
                </Box>
            </Paper>
        );
    }

    // 3. Line Chart
    if (type.toLowerCase() === 'line') {
        let xLabels = xAxis[0]?.data || config.labels || [];
        let lineSeries = series;

        if (Array.isArray(data) && data.length > 0) {
            if (!xLabels.length) {
                xLabels = data.map((d, i) => (typeof d === 'object' && d !== null ? d.name || d.label || d.category || `Point ${i + 1}` : `Point ${i + 1}`));
            }
            const values = data.map(d => (typeof d === 'object' && d !== null ? Number(d.value ?? d.amount ?? 0) : Number(d || 0)));
            lineSeries = [{ data: values, label: title || 'Amount' }];
        } else if (Array.isArray(series) && series.length > 0) {
            lineSeries = series.map((s, sIdx) => {
                const sData = Array.isArray(s.data) ? s.data : [];
                if (sData.length > 0 && typeof sData[0] === 'object' && sData[0] !== null) {
                    if (!xLabels.length) {
                        xLabels = sData.map((d, i) => d.name || d.label || d.category || `Point ${i + 1}`);
                    }
                    const numValues = sData.map(d => Number(d.value ?? d.amount ?? 0));
                    return { ...s, data: numValues, color: s.color || DEFAULT_CHART_COLORS[sIdx % DEFAULT_CHART_COLORS.length] };
                }
                const numValues = sData.map(v => Number(v || 0));
                return { ...s, data: numValues, color: s.color || DEFAULT_CHART_COLORS[sIdx % DEFAULT_CHART_COLORS.length] };
            });
        }

        const formattedXAxis = [
            {
                data: xLabels.length ? xLabels : ['Point 1'],
                scaleType: 'point',
            },
        ];

        return (
            <Paper
                elevation={0}
                sx={{
                    my: 2,
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 12px rgba(28, 28, 24, 0.04)',
                }}
            >
                {title && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: '"Manrope", sans-serif', letterSpacing: '-0.01em', mb: 1.5, color: 'text.primary' }}>
                        {title}
                    </Typography>
                )}
                <Box sx={{ width: '100%', height: 280 }}>
                    <LineChart
                        xAxis={formattedXAxis}
                        series={lineSeries}
                        height={280}
                        margin={{ top: 20, bottom: 40, left: 50, right: 20 }}
                    />
                </Box>
            </Paper>
        );
    }

    return null;
};

export default AdvisorChart;
